import React from 'react';
import { PersonChip } from 'components/common';
import PropTypes from 'prop-types';
/**
 * A person in a grid cell: avatar first, name after.
 *
 * EmployeeAvatar, not the plain Avatar the company columns use — deliberately
 * two different marks. People get the per-name colour gradient that the rest
 * of the portal already uses for owners, travelers and team members; companies
 * get the neutral default. Reading a row, the colour says "person" before the
 * name is read.
 *
 * Created-by carries the creator's uploaded photo when they have one.
 * Sales-incharge and contact are name-only in this payload — sales_incharge is
 * a free-text column with no user record behind it — so they render initials.
 */
const PersonCellRenderer = ({ value, avatarUrl }) => {
  if (!value) return <span className="tw-text-slate-300">—</span>;

  // PersonChip is the app-wide person mark (Phase 3): same gradient avatar,
  // compact short·full display, full record in the hover tooltip.
  return <PersonChip name={value} avatarUrl={avatarUrl} size="sm" className="tw-max-w-full" />;
};

PersonCellRenderer.propTypes = {
  value: PropTypes.string,
  avatarUrl: PropTypes.string,
};

/**
 * The Contact module's own default avatar: a light-blue circle with the single
 * first letter, exactly as the contact detail page draws it. Contacts wear
 * this mark everywhere they appear, so a contact in a project row is
 * recognisably the same kind of thing as the page their name links to —
 * distinct from both the employee gradient and the neutral company circle.
 */
export const ContactAvatar = ({ name, size = 22, fontSize = 10 }) => (
  <span
    className="tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-rounded-xl tw-bg-blue-100 tw-text-blue-600 tw-font-bold tw-select-none"
    style={{ width: size, height: size, fontSize }}
  >
    {(String(name).trim()[0] || '?').toUpperCase()}
  </span>
);
ContactAvatar.propTypes = {
  name: PropTypes.string,
  size: PropTypes.number,
  fontSize: PropTypes.number,
};

export const CreatedByCell = (props) => (
  <PersonCellRenderer value={props.value} avatarUrl={props.data?.created_by_avatar_url} />
);
export const SalesInchargeCell = (props) => <PersonCellRenderer value={props.value} />;
export const ContactCell = ({ value }) => {
  if (!value) return <span className="tw-text-slate-300">—</span>;

  return (
    <span className="tw-inline-flex tw-items-center tw-gap-2 tw-max-w-full">
      <ContactAvatar name={value} />
      <span className="tw-truncate">{value}</span>
    </span>
  );
};

const gridProps = { value: PropTypes.string, data: PropTypes.object };
CreatedByCell.propTypes = gridProps;
SalesInchargeCell.propTypes = gridProps;
ContactCell.propTypes = gridProps;

export default PersonCellRenderer;
